<template>
  <div id="app">
    <div
      class="sidearm-mm-loading-splash flex flex-justify-center flex-align-center"
      v-if="$root.loading_data"
    >
      <lottie-wrapper :options="lottie_options" height="300" />
    </div>
    <div v-if="$root.data">
      <main-header @navigate="go_to_section" />

      <full-page
        ref="fullpage"
        :options="options"
        :skip-init="true"
        id="fullpage"
        class="sidearm-mm-fullpage-container"
      >
        <component
          :is="hero_component"
          class="section fp-noscroll"
          :round="$root.previous_game"
        />

        <section-tournament-run
          v-if="$root.past_games && $root.past_games.length"
          class="section"
        />

        <section-selection-sunday-results class="section fp-noscroll" />

        <section-selection-sunday-reaction
          v-if="this.$root.render_reaction_section"
          class="section fp-noscroll"
        />

        <section-game-history
          v-if="this.$root.render_how_we_got_here_section"
          class="section fp-noscroll"
        />

        <component
          :is="theme_component"
          v-model="scroll_count"
          class="section"
        />

        <section-galleries
          v-if="this.$root.render_gallery_section"
          class="section fp-noscroll"
        />

        <section-social
          v-if="this.$root.render_social_section"
          class="section fp-noscroll"
        />

        <main-footer class="section fp-auto-height" />
      </full-page>
    </div>
  </div>
</template>

<script>
import LottieWrapper from "@/components/Lottie.vue";
import * as animationData from "@/assets/loading-animation.json";
import MainFooter from "@/components/MainFooter";
import MainHeader from "@/components/MainHeader";
import SectionGalleries from "@/components/Sections/SectionGalleries";
import SectionGameHistory from "@/components/Sections/SectionGameHistory";
import SectionHero from "@/components/Sections/SectionHero";
import SectionHeroWin from "@/components/Sections/SectionHeroWin";
import SectionSelectionSundayReaction from "@/components/Sections/SectionSelectionSundayReaction";
import SectionSelectionSundayResults from "@/components/Sections/SectionSelectionSundayResults";
import SectionTournamentRun from "@/components/Sections/SectionTournamentRun";
import SectionSocial from "@/components/Sections/SectionSocial";
import SectionThemeCinderella from "@/components/Sections/Themes/SectionThemeCinderella";
import SectionThemeChalk from "@/components/Sections/Themes/SectionThemeChalk";
import SectionThemeStorm from "@/components/Sections/Themes/SectionThemeStorm";
import SectionThemeBusted from "@/components/Sections/Themes/SectionThemeBusted";

import "../node_modules/fullpage.js/dist/fullpage.min.css";

export default {
  components: {
    LottieWrapper,
    MainFooter,
    MainHeader,
    SectionGalleries,
    SectionGameHistory,
    SectionHero,
    SectionHeroWin,
    SectionSelectionSundayReaction,
    SectionSelectionSundayResults,
    SectionTournamentRun,
    SectionSocial,
    SectionThemeCinderella,
    SectionThemeChalk,
    SectionThemeStorm,
    SectionThemeBusted
  },

  data: function() {
    return {
      fullpage_initialized: false,

      options: {
        loopHorizontal: false,
        controlArrows: false,
        slidesNavigation: true,
        onSlideLeave: this.onSlideLeave,
        onLeave: this.onLeave,
        afterLoad: this.afterLoad,
        normalScrollElements: "[scrolling]",
        scrollOverflow: true,
        scrollOverflowOptions: {
          bounce: false,
          scrollbars: false,
          indicators: []
        }
      },
      theme_component: "",
      allow_manual_transition: false,
      scroll_count: 0,
      lottie_options: {
        animationData: animationData.default,
        loop: false,
        rendererSettings: {
          progressiveLoad: true
        }
      }
    };
  },

  computed: {
    hero_component() {
      if (!this.$root.data) {
        return "section-hero";
      }

      if (this.$root.previous_game) {
        return "section-hero-win";
      }

      return "section-hero";
    }
  },

  watch: {
    "$root.data"(newval) {
      if (newval) {
        this.set_theme_section();
        this.init_fullpage();
        this.$nextTick(() => {
          this.$root.fullpage_ref = this.$refs.fullpage;
        });
      }
    }
  },

  methods: {
    init_fullpage() {
      this.$nextTick(() => {
        if (this.fullpage_initialized) {
          this.$refs.fullpage.api.reBuild();
        } else {
          this.$refs.fullpage.init();

          this.fullpage_initialized = true;

          window.addEventListener("resize", () => {
            this.$nextTick(() => {
              this.$nextTick(() => {
                this.$refs.fullpage.api.reBuild();
              });
            });
          });
        }
      });
    },

    onLeave(origin, destination, direction) {
      if (!this.$root.move_via_nav) {
        if (window.matchMedia("(min-width:1025px)").matches) {
          if (
            this.$refs.fullpage &&
            this.$refs.fullpage.api &&
            this.$refs.fullpage.api.getActiveSlide()
          ) {
            let active_slide = this.$refs.fullpage.api.getActiveSlide();
            if (direction === "up" && !active_slide.isFirst) {
              this.$refs.fullpage.api.moveSlideLeft();
              return false;
            } else if (direction === "down" && !active_slide.isLast) {
              this.$refs.fullpage.api.moveSlideRight();
              return false;
            }
          }
        }
      } else {
        this.$root.move_via_nav = false;
      }

      if (
        origin.anchor === "theme" &&
        this.theme_component === "section-theme-chalk"
      ) {
        if (direction === "down") {
          this.scroll_count++;
        }
        if (direction === "up") {
          this.scroll_count--;
        }

        if (this.scroll_count >= 25) {
          this.scroll_count = 25;
          return true;
        }

        if (this.scroll_count <= 0) {
          this.scroll_count = 0;
          return true;
        }
        return false;
      }
    },

    afterLoad() {
      this.$nextTick(() => {
        this.$root.active_anchor = this.$refs.fullpage.api.getActiveSection().anchor;
      });
    },

    onSlideLeave(section, origin, destination, direction) {
      if (section.anchor === "featured-games" || section.anchor === "gallery") {
        if (destination.isLast && direction === "right") {
          section.item.classList.add("on-transition-slide");
        } else {
          section.item.classList.remove("on-transition-slide");
        }
      }
    },

    go_to_section(anchor) {
      this.$refs.fullpage.api.moveTo(anchor);
    },

    set_theme_section() {
      if (!this.$root.data) {
        return "";
      }

      const theme = this.$root.data.team.theme || "cinderella";

      this.theme_component = `section-theme-${theme}`;

      if (theme === "cinderella") {
        this.options.scrollOverflowOptions.indicators = [
          {
            el: "#sidearm-mm-theme-glass-shoe",
            resize: false,
            ignoreBoundaries: true,
            listenY: true,
            listenX: false,
            speedRatioY: -1.6
          },
          {
            el: "#sidearm-mm-theme-glass-blur-top",
            resize: false,
            ignoreBoundaries: true,
            listenY: true,
            listenX: false,
            speedRatioY: 0.4
          },
          {
            el: "#sidearm-mm-theme-glass-group",
            resize: false,
            ignoreBoundaries: true,
            listenY: true,
            listenX: false,
            speedRatioY: 0.5
          },
          {
            el: "#sidearm-mm-theme-glass-group-b",
            resize: false,
            ignoreBoundaries: true,
            listenY: true,
            listenX: false,
            speedRatioY: 0.5
          },
          {
            el: "#sidearm-mm-theme-blue-particle",
            resize: false,
            ignoreBoundaries: true,
            listenY: true,
            listenX: false,
            speedRatioY: 0.5
          },
          {
            el: "#sidearm-mm-theme-title",
            resize: false,
            ignoreBoundaries: true,
            listenY: true,
            listenX: false,
            speedRatioY: -1.4
          },
          {
            el: "#sidearm-mm-theme-divider",
            resize: false,
            ignoreBoundaries: true,
            listenY: true,
            listenX: false,
            speedRatioY: -2
          }
        ];
      } else if (theme === "busted") {
        this.options.scrollOverflowOptions.indicators = [
          {
            el: "#sidearm-mm-theme-bg",
            resize: false,
            ignoreBoundaries: true,
            listenY: true,
            listenX: false,
            speedRatioY: window.matchMedia("(min-width:769px)").matches
              ? -0.6
              : -0.95
          },
          {
            el: "#sidearm-mm-theme-cloud-top",
            resize: false,
            ignoreBoundaries: true,
            listenY: true,
            listenX: false,
            speedRatioY: -0.4
          },
          {
            el: "#sidearm-mm-theme-cloud-bottom",
            resize: false,
            ignoreBoundaries: true,
            listenY: true,
            listenX: false,
            speedRatioY: -1.6
          },
          {
            el: "#sidearm-mm-theme-court",
            resize: false,
            ignoreBoundaries: true,
            listenY: true,
            listenX: false,
            speedRatioY: -0.3
          },
          {
            el: "#sidearm-mm-theme-title",
            resize: false,
            ignoreBoundaries: true,
            listenY: true,
            listenX: false,
            speedRatioY: -1.4
          },
          {
            el: "#sidearm-mm-theme-x-top",
            resize: false,
            ignoreBoundaries: true,
            listenY: true,
            listenX: false,
            speedRatioY: -1.25
          },
          {
            el: "#sidearm-mm-theme-x-bottom",
            resize: false,
            ignoreBoundaries: true,
            listenY: true,
            listenX: false,
            speedRatioY: -1.15
          },
          {
            el: "#sidearm-mm-theme-divider",
            resize: false,
            ignoreBoundaries: true,
            listenY: true,
            listenX: false,
            speedRatioY: -2
          }
        ];
      } else if (theme === "storm") {
        this.options.scrollOverflowOptions.indicators = [
          {
            el: "#sidearm-mm-theme-bg",
            resize: false,
            ignoreBoundaries: true,
            listenY: true,
            listenX: false,
            speedRatioY: 0.1
          },
          {
            el: "#sidearm-mm-theme-bottom-lightning",
            resize: false,
            ignoreBoundaries: true,
            listenY: true,
            listenX: false,
            speedRatioY: -0.25
          },
          {
            //
            el: "#sidearm-mm-theme-jersey",
            resize: false,
            ignoreBoundaries: true,
            listenY: true,
            listenX: false,
            speedRatioY: -1.3
          },
          {
            el: "#sidearm-mm-theme-title",
            resize: false,
            ignoreBoundaries: true,
            listenY: true,
            listenX: false,
            speedRatioY: -0.2
          },
          {
            el: "#sidearm-mm-theme-top-lightning",
            resize: false,
            ignoreBoundaries: true,
            listenY: true,
            listenX: false,
            speedRatioY: 1.2
          },
          {
            //
            el: "#sidearm-mm-theme-top-smoke",
            resize: false,
            ignoreBoundaries: true,
            listenY: true,
            listenX: false,
            speedRatioY: -0.95
          },
          {
            //
            el: "#sidearm-mm-theme-divider",
            resize: false,
            ignoreBoundaries: true,
            listenY: true,
            listenX: false,
            speedRatioY: -2
          }
        ];
      }
    }
  },

  mounted() {
    if (this.$root.data) {
      this.$root.fullpage_ref = this.$refs.fullpage;
      this.set_theme_section();
      this.init_fullpage();
    }
  }
};
</script>

<style lang="scss" scoped>
.sidearm-mm {
  &-fullpage-container {
    height: 100vh !important;
    width: 100vw !important;
  }
  &-loading-splash {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: #000;
    z-index: 99999;
  }
}
</style>

<style lang="scss">
@import "@/styles/app.scss";

.fp-tableCell {
  display: block;
}
</style>

<style>
/* TODO-KL Forgot to prevent the tracking tags from being injected, notification bell */
.ss-7722b-container {
  display: none;
}
.ma-05 {
  margin: 6px;
}

.mx-05 {
  margin-left: 6px;
  margin-right: 6px;
}

.my-05 {
  margin-top: 6px;
  margin-bottom: 6px;
}

.pa-05 {
  padding: 6px;
}

.px-05 {
  padding-left: 6px;
  padding-right: 6px;
}

.py-05 {
  padding-top: 6px;
  padding-bottom: 6px;
}

.mt-05 {
  margin-top: 6px;
}

.pt-05 {
  padding-top: 6px;
}

.mr-05 {
  margin-right: 6px;
}

.pr-05 {
  padding-right: 6px;
}

.mb-05 {
  margin-bottom: 6px;
}

.pb-05 {
  padding-bottom: 6px;
}

.ml-05 {
  margin-left: 6px;
}

.pl-05 {
  padding-left: 6px;
}
</style>
